// P1: Profile Polish - Edit Profile Page
import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Save, User, Loader, Video, X, Eye, Sparkles, Lock, Users, Image } from "lucide-react";
import { AuthContext } from "../App";
import apiClient from "../api/client";
import { toast } from "sonner";

// PATCH: Fix BACKEND_URL ReferenceError by deriving backend origin from apiClient baseURL
const getBackendOrigin = () => {
  const base = apiClient?.defaults?.baseURL || "";
  // base is typically like: https://host/api  → we want https://host
  return base.replace(/\/api\/?$/, "");
};
import { RelationshipProfileSection } from "../components/profile/RelationshipProfileSection";
import { ChildrenSection } from "../components/profile/ChildrenSection";
import { GlobalNav } from "../components/GlobalNav";
import { ProfileViewModal } from "../components/profile/ProfileViewModal";
import { getProfilePhotos, getPhotoCount } from "../utils/profileMedia";
import { normalizeList } from "../utils/normalize";
import InlineNotice from "../components/common/InlineNotice";
import { useInlineNotice } from "../hooks/useInlineNotice";
import { track } from "../utils/analytics";

// 🔧 FIX Issue #2: Video Intro Section Component
// Patch 3: Hardened video upload with type="button" and strict validation
function VideoIntroSection({ videoUrl, onVideoUpdate, uploading, token }) {
  const fileInputRef = useRef(null);
  const [error, setError] = useState("");

  const openPicker = () => fileInputRef.current?.click();

  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Patch 3: Strong compatibility stance for beta testers
    const isMp4 = file.type === "video/mp4" || file.name.toLowerCase().endsWith(".mp4");
    const isWebm = file.type === "video/webm" || file.name.toLowerCase().endsWith(".webm");
    const isMov = file.type === "video/quicktime" || file.name.toLowerCase().endsWith(".mov");

    if (!isMp4 && !isWebm && !isMov) {
      setError('Please select MP4 (recommended), WEBM, or MOV video');
      toast.error('Please select MP4 (recommended), WEBM, or MOV video');
      event.target.value = "";
      return;
    }

    // Warn about MOV compatibility
    if (isMov && !isMp4) {
      toast.info('MOV files may not play on all devices. MP4 is recommended for best compatibility.');
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setError('Video must be smaller than 50MB');
      toast.error('Video must be smaller than 50MB');
      event.target.value = "";
      return;
    }

    setError("");
    onVideoUpdate(null, true); // Start upload

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await apiClient.post(`/profiles/me/video-intro`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      onVideoUpdate(res.data.video_intro_url, false);
      toast.success("Video intro uploaded successfully!");
    } catch (e) {
      console.error(e);
      const errorMsg = e?.response?.data?.detail || "Could not upload video. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg);
      onVideoUpdate(null, false);
    } finally {
      event.target.value = ""; // Allow re-uploading same file
    }
  };

  const handleRemoveVideo = async () => {
    setError("");
    onVideoUpdate(null, true); // Start removal
    
    try {
      await apiClient.delete(`/profiles/me/video-intro`);
      onVideoUpdate(null, false);
      toast.success("Video intro removed");
    } catch (e) {
      console.error(e);
      const errorMsg = e?.response?.data?.detail || "Could not remove video. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  return (
    <section id="video-intro" className="rounded-lg bg-gray-900/60 border border-gray-700 p-6">
      <h2 className="mb-2 text-lg font-semibold text-white flex items-center gap-2">
        <Video className="w-5 h-5" />
        Video Intro
      </h2>
      <p className="mb-4 text-xs text-gray-400">
        Record a short video (up to 30 seconds) to introduce yourself! This helps others get to know your personality.
        <span className="block mt-1 text-purple-400">💡 Tip: MP4 format works best on all devices.</span>
      </p>

      {videoUrl ? (
        <div className="space-y-3">
          {/* Video Preview - Fixed aspect ratio, no cropping */}
          <div className="aspect-video max-w-md rounded-lg border border-gray-600 bg-black overflow-hidden">
            <video
              src={videoUrl}
              controls
              playsInline
              preload="metadata"
              className="w-full h-full object-contain"
            />
          </div>
          
          {/* Actions - Patch 3: Ensure type="button" prevents form submission */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={openPicker}
              disabled={uploading}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? "Uploading..." : "Replace Video"}
            </button>
            <button
              type="button"
              onClick={handleRemoveVideo}
              disabled={uploading}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          disabled={uploading}
          className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-lg border-2 border-dashed border-gray-600 text-gray-300 hover:border-purple-500 hover:text-purple-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Video className="w-5 h-5" />
          <span>{uploading ? "Uploading..." : "Upload Video Intro"}</span>
        </button>
      )}

      {error && (
        <p className="mt-2 text-xs text-red-400">{error}</p>
      )}

      {/* Hidden File Input - Patch 3: Accept specific video types */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
        onChange={handleVideoUpload}
        className="hidden"
      />
    </section>
  );
}

// Couple Names Section - Only shown when profile is a couple
function CoupleNamesSection({ names, onNamesChange }) {
  // names is an array like ["Partner 1 Name", "Partner 2 Name"]
  const name1 = names?.[0] || "";
  const name2 = names?.[1] || "";

  const handleName1Change = (e) => {
    const newNames = [e.target.value, name2];
    onNamesChange(newNames);
  };

  const handleName2Change = (e) => {
    const newNames = [name1, e.target.value];
    onNamesChange(newNames);
  };

  return (
    <section id="couple-names" className="rounded-lg bg-gray-900/60 border border-gray-700 p-6">
      <h2 className="mb-2 text-lg font-semibold text-white flex items-center gap-2">
        <Users className="w-5 h-5" />
        Couple Profile
      </h2>
      <p className="mb-4 text-xs text-gray-400">
        Enter the names for both partners. These will be shown together on your profile (e.g., &quot;John &amp; Jane&quot;).
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="partner1_name" className="block text-sm font-medium text-gray-300 mb-2">
            Partner 1 Name
          </label>
          <input
            type="text"
            id="partner1_name"
            value={name1}
            onChange={handleName1Change}
            placeholder="First partner's name"
            className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
          />
        </div>
        <div>
          <label htmlFor="partner2_name" className="block text-sm font-medium text-gray-300 mb-2">
            Partner 2 Name
          </label>
          <input
            type="text"
            id="partner2_name"
            value={name2}
            onChange={handleName2Change}
            placeholder="Second partner's name"
            className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
          />
        </div>
      </div>

      {name1 && name2 && (
        <p className="mt-3 text-sm text-purple-300">
          Your profile will show: <strong>{name1} & {name2}</strong>
        </p>
      )}
    </section>
  );
}

// Private Photos Section - Separate gallery for private content
function PrivatePhotosSection({ privatePhotos, onPhotosUpdate, uploading, onUploadingChange, token }) {
  const fileInputRef = useRef(null);
  const [error, setError] = useState("");
  
  // Normalize to array, max 3 private photos
  const photos = normalizeList(privatePhotos).slice(0, 3);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if already at max
    if (photos.length >= 3) {
      toast.error("Maximum 3 private photos allowed");
      return;
    }

    setError("");
    onUploadingChange(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("is_private", "true");

    try {
      const res = await apiClient.post(`/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      const photoUrl = res.data.file_url;
      const newPhotos = [...photos, photoUrl];
      onPhotosUpdate(newPhotos);
      toast.success("Private photo uploaded!");
    } catch (e) {
      console.error(e);
      const errorMsg = e?.response?.data?.detail || "Could not upload photo. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      onUploadingChange(false);
      event.target.value = "";
    }
  };

  const handleRemovePhoto = (indexToRemove) => {
    const newPhotos = photos.filter((_, idx) => idx !== indexToRemove);
    onPhotosUpdate(newPhotos);
    toast.success("Private photo removed");
  };

  return (
    <section id="private-photos" className="rounded-lg bg-gray-900/60 border border-amber-700/50 p-6">
      <h2 className="mb-2 text-lg font-semibold text-white flex items-center gap-2">
        <Lock className="w-5 h-5 text-amber-400" />
        Private Photos
        <span className="ml-auto text-xs text-amber-400 bg-amber-900/30 px-2 py-1 rounded-full">
          {photos.length}/3
        </span>
      </h2>
      <p className="mb-4 text-xs text-gray-400">
        <span className="text-amber-300">🔒 Visible only to matches you choose to share them with.</span>{" "}
        Private photos never appear in discovery or public profiles.
      </p>

      {/* Photo Grid */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {photos.map((photoUrl, idx) => (
          <div key={idx} className="relative group aspect-square">
            <img
              src={photoUrl.startsWith('/') ? `${getBackendOrigin()}${photoUrl}` : photoUrl}
              alt={`Private photo ${idx + 1}`}
              className="w-full h-full object-cover rounded-lg border border-amber-600/30"
            />
            <button
              type="button"
              onClick={() => handleRemovePhoto(idx)}
              className="absolute top-1 right-1 p-1 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove photo"
            >
              <X className="w-3 h-3" />
            </button>
            <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-amber-900/80 text-amber-200 text-[10px]">
              <Lock className="w-2.5 h-2.5 inline mr-0.5" />
              Private
            </div>
          </div>
        ))}
        
        {/* Add Photo Button */}
        {photos.length < 3 && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="aspect-square flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-amber-600/40 text-amber-400 hover:border-amber-500 hover:text-amber-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Image className="w-5 h-5" />
                <span className="text-[10px]">Add Private</span>
              </>
            )}
          </button>
        )}
      </div>

      {error && <p className="text-xs text-red-400 mt-2">{error}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </section>
  );
}

// Private Video Section
function PrivateVideoSection({ privateVideos, onVideoUpdate, uploading, onUploadingChange, token }) {
  const fileInputRef = useRef(null);
  const [error, setError] = useState("");
  
  // Only 1 private video allowed
  const videos = normalizeList(privateVideos);
  const currentVideo = videos[0] || null;

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate video type
    const isMp4 = file.type === "video/mp4" || file.name.toLowerCase().endsWith(".mp4");
    const isWebm = file.type === "video/webm" || file.name.toLowerCase().endsWith(".webm");
    const isMov = file.type === "video/quicktime" || file.name.toLowerCase().endsWith(".mov");

    if (!isMp4 && !isWebm && !isMov) {
      setError('Please select MP4, WEBM, or MOV video');
      toast.error('Please select MP4, WEBM, or MOV video');
      event.target.value = "";
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Video must be smaller than 50MB');
      toast.error('Video must be smaller than 50MB');
      event.target.value = "";
      return;
    }

    setError("");
    onUploadingChange(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("is_private", "true");
    formData.append("media_type", "video");

    try {
      const res = await apiClient.post(`/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      const videoUrl = res.data.file_url;
      onVideoUpdate([videoUrl]);
      toast.success("Private video uploaded!");
    } catch (e) {
      console.error(e);
      const errorMsg = e?.response?.data?.detail || "Could not upload video. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      onUploadingChange(false);
      event.target.value = "";
    }
  };

  const handleRemoveVideo = () => {
    onVideoUpdate([]);
    toast.success("Private video removed");
  };

  const videoSrc = currentVideo 
    ? (currentVideo.startsWith('/') ? `${getBackendOrigin()}${currentVideo}` : currentVideo)
    : null;

  return (
    <section id="private-video" className="rounded-lg bg-gray-900/60 border border-amber-700/50 p-6">
      <h2 className="mb-2 text-lg font-semibold text-white flex items-center gap-2">
        <Lock className="w-5 h-5 text-amber-400" />
        <Video className="w-5 h-5" />
        Private Video
      </h2>
      <p className="mb-4 text-xs text-gray-400">
        <span className="text-amber-300">🔒 Visible only to matches you choose to share them with.</span>{" "}
        Upload a private video intro that never appears in discovery or public profiles. Max 1 video, 50MB.
      </p>

      {videoSrc ? (
        <div className="space-y-3">
          <div className="aspect-video max-w-md rounded-lg border border-amber-600/30 bg-black overflow-hidden relative">
            <video
              src={videoSrc}
              controls
              playsInline
              preload="metadata"
              className="w-full h-full object-contain"
            />
            <div className="absolute top-2 left-2 px-2 py-1 rounded bg-amber-900/80 text-amber-200 text-xs">
              <Lock className="w-3 h-3 inline mr-1" />
              Private
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm hover:bg-amber-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? "Uploading..." : "Replace Video"}
            </button>
            <button
              type="button"
              onClick={handleRemoveVideo}
              disabled={uploading}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-lg border-2 border-dashed border-amber-600/40 text-amber-400 hover:border-amber-500 hover:text-amber-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Video className="w-5 h-5" />
          <span>{uploading ? "Uploading..." : "Upload Private Video"}</span>
        </button>
      )}

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
        className="hidden"
        onChange={handleFileChange}
      />
    </section>
  );
}

// V2 Single Photo System - Main Photo Section Component
function MainPhotoSection({ avatarUrl, avatarUpdatedAt, onAvatarUpdate, token }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError("");
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      // Use the new /upload endpoint with media pipeline
      const res = await apiClient.post(`/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      // Update profile with the new photo URL
      const photoUrl = res.data.file_url;
      const saved = await apiClient.post(`/me/profile`, { main_photo_url: photoUrl });
      
      // ✅ Trust server response (prevents "looks saved but isn't")
      const persisted = saved?.data?.profile?.main_photo_url || photoUrl;
      onAvatarUpdate(persisted, new Date().toISOString());
      toast.success("Photo uploaded successfully!");
    } catch (e) {
      console.error(e);
      const errorMsg = e?.response?.data?.detail || "Could not upload photo. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleRemove = async () => {
    setError("");
    setUploading(true);
    try {
      await apiClient.delete(`/profile/avatar`);
      onAvatarUpdate(null, null);
      toast.success("Photo removed");
    } catch (e) {
      console.error(e);
      const errorMsg = e?.response?.data?.detail || "Could not remove photo. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const hasAvatar = !!avatarUrl;

  return (
    <section id="main-photo" className="rounded-lg bg-gray-900/60 border border-gray-700 p-6">
      <h2 className="mb-2 text-lg font-semibold text-white">Main Photo</h2>
      <p className="mb-4 text-xs text-gray-400">
        This is the photo people see in Quick Match, your matches list, and the
        Lounge. Choose a clear photo that shows your face.
      </p>

      <div className="flex items-start gap-4">
        {hasAvatar ? (
          <div className="relative">
            <img
              src={avatarUrl}
              alt="Main profile"
              className="h-24 w-24 rounded-2xl object-cover border border-gray-600"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex h-24 w-24 cursor-pointer items-center justify-center rounded-2xl border-2 border-dashed border-gray-600 text-[11px] text-gray-400 hover:border-sky-500 hover:text-sky-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={uploading}
          >
            <span className="text-center px-2">
              {uploading ? "Uploading..." : "Tap to add photo"}
            </span>
          </button>
        )}

        <div className="flex flex-col gap-2 text-xs">
          {hasAvatar && (
            <>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-600 px-3 py-1.5 text-xs font-medium text-white hover:border-sky-500 hover:text-sky-300 transition disabled:opacity-50 disabled:cursor-wait"
                disabled={uploading}
              >
                {uploading && <Loader className="w-3 h-3 animate-spin" />}
                {uploading ? "Uploading..." : "Change photo"}
              </button>
              <button
                type="button"
                onClick={handleRemove}
                className="text-[11px] text-gray-400 hover:text-rose-400 transition disabled:opacity-50"
                disabled={uploading}
              >
                Remove photo
              </button>
              {avatarUpdatedAt && (
                <p className="text-[10px] text-gray-500">
                  Last updated: {new Date(avatarUpdatedAt).toLocaleDateString()}
                </p>
              )}
            </>
          )}

          {!hasAvatar && (
            <p className="text-[11px] text-gray-500 max-w-[200px]">
              No photo added yet. Tap the box to upload your main photo.
            </p>
          )}

          {error && <p className="text-[11px] text-rose-400">{error}</p>}
        </div>
      </div>

      {/* Single unified file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </section>
  );
}

export function EditProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUpdatedAt, setAvatarUpdatedAt] = useState(null);
  
  // Use InlineNotice hook for consistent error handling
  const { notice, showError, showSuccess, dismissNotice } = useInlineNotice("profile-edit", token);
  
  // Legacy state for backward compatibility
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // 🔧 FIX Issue #2: Video Intro State
  const [videoIntroUrl, setVideoIntroUrl] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const videoInputRef = useRef(null);
  
  // Couple Names State (for couple profiles)
  const [coupleNames, setCoupleNames] = useState([]);
  
  // Private Media State
  const [privatePhotos, setPrivatePhotos] = useState([]);
  const [privateVideos, setPrivateVideos] = useState([]);
  const [uploadingPrivatePhoto, setUploadingPrivatePhoto] = useState(false);
  const [uploadingPrivateVideo, setUploadingPrivateVideo] = useState(false);
  
  // 👁️ Profile Preview State
  const [showPreview, setShowPreview] = useState(false);
  const [fullProfile, setFullProfile] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    display_name: "",
    date_of_birth: "",
    gender: "",
    couple_type: "",
    looking_for: [],
    relationship_style: "",
    location_city: "",
    location_region: "",
    bio: "",
    interests: "",
    // Children & Family fields
    has_children: null,
    children_count: null,
    children_age_ranges: [],
    custody_type: null,
    homeschooling_status: null,
    // Intent Signal v1
    intent_signal: null,
  });

  // Load existing profile
  useEffect(() => {
    async function loadProfile() {
      try {
        const response = await apiClient.get(`/me/profile`);
        
        const profile = response.data.profile || {};
        
        // Store full profile for preview
        setFullProfile({
          ...profile,
          names: [profile.display_name || user?.name || "User"],
          ages: profile.date_of_birth ? [calculateAge(profile.date_of_birth)] : [],
          location: [profile.location_city, profile.location_region].filter(Boolean).join(", ") || null,
          is_elite: user?.is_elite || false,
          is_age_verified: user?.is_age_verified || false,
          badges: user?.badges || [],
        });
        
        setFormData({
          display_name: profile.display_name || "",
          date_of_birth: profile.date_of_birth || "",
          gender: profile.gender || "",
          couple_type: profile.couple_type || "",
          looking_for: profile.looking_for || [],
          relationship_style: profile.relationship_style || "",
          location_city: profile.location_city || "",
          location_region: profile.location_region || "",
          bio: profile.bio || "",
          interests: Array.isArray(profile.interests) ? profile.interests.join(", ") : "",
          // Children & Family fields
          has_children: profile.has_children || null,
          children_count: profile.children_count || null,
          children_age_ranges: profile.children_age_ranges || [],
          custody_type: profile.custody_type || null,
          homeschooling_status: profile.homeschooling_status || null,
          // Intent Signal v1
          intent_signal: profile.intent_signal || null,
        });
        
        // ✅ Source of truth: profile.main_photo_url (persisted server-side)
        const resolvedMainPhoto =
          profile?.main_photo_url ||
          user?.avatar_url || // fallback for legacy users
          null;

        setAvatarUrl(resolvedMainPhoto);
        // If backend doesn't provide updated_at, at least set something stable-ish
        setAvatarUpdatedAt(profile?.main_photo_updated_at || user?.avatar_updated_at || null);
        
        // 🔧 FIX Issue #2: Load video intro URL
        setVideoIntroUrl(profile.video_intro_url || null);
        
        // Load couple names (if profile is a couple type)
        setCoupleNames(normalizeList(profile.names));
        
        // Load private media
        setPrivatePhotos(normalizeList(profile.private_photos));
        setPrivateVideos(normalizeList(profile.private_videos));
      } catch (err) {
        console.error("Failed to load profile:", err);
        showError(err, { 
          customMessage: "Couldn't load your profile. Please try again.",
          onRetry: loadProfile,
        });
      } finally {
        setLoading(false);
      }
    }
    
    // Helper to calculate age from DOB
    function calculateAge(dob) {
      if (!dob) return null;
      const birth = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return age;
    }

    loadProfile();
  }, [token, user, showError]);

  // Scroll to hash target after loading (e.g., #main-photo)
  useEffect(() => {
    if (!loading && location.hash) {
      const targetId = location.hash.replace('#', '');
      const element = document.getElementById(targetId);
      if (element) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add highlight effect
          element.classList.add('ring-2', 'ring-sky-500', 'ring-offset-2', 'ring-offset-gray-900');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-sky-500', 'ring-offset-2', 'ring-offset-gray-900');
          }, 2000);
        }, 300);
      }
    }
  }, [loading, location.hash]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLookingForToggle = (option) => {
    setFormData(prev => {
      const current = prev.looking_for || [];
      if (current.includes(option)) {
        return { ...prev, looking_for: current.filter(item => item !== option) };
      } else {
        return { ...prev, looking_for: [...current, option] };
      }
    });
  };

  // === Payload sanitization helpers ===
  const cleanString = (v) => {
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    return s.length ? s : null;
  };

  const cleanSelect = (v) => {
    const s = cleanString(v);
    if (!s) return null;

    const lowered = s.toLowerCase();
    if (
      lowered === "select gender" ||
      lowered === "select style" ||
      lowered.startsWith("select ")
    ) {
      return null;
    }
    return s;
  };

  const cleanStringArray = (arr) => {
    if (!Array.isArray(arr)) return null;
    const out = arr.map(cleanString).filter(Boolean);
    return out.length ? out : null;
  };

  const cleanInt = (v) => {
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  };

  // Submit logic separated from DOM event so retries are safe
  const submitProfile = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    dismissNotice();

    try {
      const interests = (formData.interests || "")
        .split(",")
        .map((i) => i.trim())
        .filter((i) => i.length > 0);

      const gender = cleanSelect(formData.gender);
      const relationshipStyle = cleanSelect(formData.relationship_style);

      const hasChildren = cleanSelect(formData.has_children);
      const childrenCount = cleanInt(formData.children_count);

      const payload = {
        display_name: cleanString(formData.display_name),
        date_of_birth: cleanString(formData.date_of_birth),
        gender,
        couple_type: gender === "couple" ? cleanSelect(formData.couple_type) : null,
        looking_for: cleanStringArray(formData.looking_for),
        relationship_style: relationshipStyle,
        location_city: cleanString(formData.location_city),
        location_region: cleanString(formData.location_region),
        bio: cleanString(formData.bio),
        interests: interests.length ? interests : null,

        has_children: hasChildren,
        children_count: hasChildren && hasChildren !== "no" ? childrenCount : null,
        children_age_ranges: cleanStringArray(formData.children_age_ranges),
        custody_type: cleanSelect(formData.custody_type),
        homeschooling_status: cleanSelect(formData.homeschooling_status),

        // Intent Signal v1 - only include if set (prevents accidental wipes)
        intent_signal: formData.intent_signal || null,

        names:
          gender === "couple" && Array.isArray(coupleNames)
            ? coupleNames.map(cleanString).filter(Boolean).length
              ? coupleNames.map(cleanString).filter(Boolean)
              : null
            : null,

        private_photos:
          Array.isArray(privatePhotos) ? privatePhotos : [],
        private_videos:
          Array.isArray(privateVideos) ? privateVideos : [],
      };

      // Best-effort GPS capture (no external APIs)
      const getGeoPoint = () =>
        new Promise((resolve) => {
          if (!navigator.geolocation) return resolve(null);

          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const lat = pos?.coords?.latitude;
              const lon = pos?.coords?.longitude;
              if (typeof lat === "number" && typeof lon === "number") {
                resolve({ type: "Point", coordinates: [lon, lat] }); // GeoJSON: [lon, lat]
              } else {
                resolve(null);
              }
            },
            () => resolve(null),
            {
              enableHighAccuracy: false,
              timeout: 3500,
              maximumAge: 1000 * 60 * 30, // 30 min cache
            }
          );
        });

      const geo = await getGeoPoint();
      if (geo) {
        payload.location_geo = geo;
      }

      await apiClient.post(`/me/profile`, payload);

      // ✅ Critical: re-hydrate from server so UI cannot "snap back" to stale values
      const refreshed = await apiClient.get(`/me/profile`);

      const serverProfile = refreshed?.data?.profile || {};

      // Update local form state with server truth (especially display_name)
      setFormData((prev) => ({
        ...prev,
        display_name: serverProfile.display_name ?? prev.display_name,
        date_of_birth: serverProfile.date_of_birth ?? prev.date_of_birth,
        gender: serverProfile.gender ?? prev.gender,
        couple_type: serverProfile.couple_type ?? prev.couple_type,
        relationship_style: serverProfile.relationship_style ?? prev.relationship_style,
        location_city: serverProfile.location_city ?? prev.location_city,
        location_region: serverProfile.location_region ?? prev.location_region,
        bio: serverProfile.bio ?? prev.bio,
        interests: Array.isArray(serverProfile.interests)
          ? serverProfile.interests.join(", ")
          : prev.interests,
        has_children: serverProfile.has_children ?? prev.has_children,
        children_count: serverProfile.children_count ?? prev.children_count,
        children_age_ranges: serverProfile.children_age_ranges ?? prev.children_age_ranges,
        custody_type: serverProfile.custody_type ?? prev.custody_type,
        homeschooling_status: serverProfile.homeschooling_status ?? prev.homeschooling_status,
        looking_for: serverProfile.looking_for ?? prev.looking_for,
        // Intent Signal v1
        intent_signal: serverProfile.intent_signal ?? prev.intent_signal,
      }));

      // Track intent_signal_set event for analytics
      if (serverProfile.intent_signal) {
        track("intent_signal_set", { intent_signal: serverProfile.intent_signal });
      }

      showSuccess("Your changes have been saved.", { title: "Profile updated!" });
      toast.success("Saved — your profile is updated.");
    } catch (err) {
      console.error("Failed to update profile:", err);

      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Couldn't save your profile. Please try again.";

      showError(err, {
        customMessage: detail,
        onRetry: () => submitProfile(),
      });

      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitProfile();
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <div className="text-white">Loading profile...</div>
      </div>
    );
  }

  return (
    <>
      {/* 🔧 FIX Issue #1: Add Global Navigation */}
      <GlobalNav />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 px-4 pt-20 pb-24">
        <div className="mx-auto max-w-2xl relative z-0">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="inline-flex items-center space-x-2 rounded-full border border-gray-700 bg-gray-900 px-4 py-2 text-sm text-gray-100 hover:bg-gray-800 transition"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Dashboard</span>
            </button>
            
            <div className="flex items-center space-x-3">
              {/* Preview My Profile Button */}
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="inline-flex items-center space-x-2 rounded-full border border-purple-500/50 bg-purple-900/30 px-4 py-2 text-sm text-purple-200 hover:bg-purple-900/50 transition"
                title="See what others see"
              >
                <Eye className="h-4 w-4" />
                <span>Preview</span>
              </button>
              
              <div className="flex items-center space-x-2 text-purple-300">
                <User className="h-5 w-5" />
                <span className="text-sm font-medium">Edit Profile</span>
              </div>
            </div>
          </div>
          
          {/* Debug: Media Status (admin-only) */}
          {user?.is_admin && (
          <div className="mb-4 rounded-lg bg-slate-800/50 border border-slate-700 px-4 py-2 text-xs text-slate-400 flex items-center gap-4 flex-wrap">
            <span>Photos: {getPhotoCount(fullProfile)}</span>
            <span>Video: {videoIntroUrl ? "Yes" : "No"}</span>
            <span>Avatar: {avatarUrl ? "Set" : "None"}</span>
            <span className="text-amber-400">Private Photos: {privatePhotos.length}/3</span>
            <span className="text-amber-400">Private Video: {privateVideos.length > 0 ? "Yes" : "No"}</span>
          </div>
          )}

          {/* Profile Setup Checklist - shows only when profile is incomplete */}
          {(() => {
            const hasPhoto = getPhotoCount(fullProfile) > 0 || avatarUrl;
            const hasName = formData.display_name?.trim();
            const hasRelStyle = formData.relationship_style;
            const hasBio = formData.bio?.trim();
            const isComplete = hasPhoto && hasName && hasRelStyle && hasBio;
            
            if (isComplete) return null;
            
            return (
              <section id="setup" className="mb-6 rounded-lg bg-amber-900/20 border border-amber-500/30 p-4">
                <h3 className="text-sm font-semibold text-amber-200 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Complete Your Profile
                </h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${hasPhoto ? 'bg-green-900/30 text-green-300' : 'bg-slate-800/50 text-slate-400'}`}>
                    {hasPhoto ? '✓' : '○'} Add a photo
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${hasName ? 'bg-green-900/30 text-green-300' : 'bg-slate-800/50 text-slate-400'}`}>
                    {hasName ? '✓' : '○'} Set display name
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${hasRelStyle ? 'bg-green-900/30 text-green-300' : 'bg-slate-800/50 text-slate-400'}`}>
                    {hasRelStyle ? '✓' : '○'} Choose relationship style
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${hasBio ? 'bg-green-900/30 text-green-300' : 'bg-slate-800/50 text-slate-400'}`}>
                    {hasBio ? '✓' : '○'} Write about yourself
                  </div>
                </div>
              </section>
            );
          })()}

        {/* InlineNotice for consistent error/success handling */}
        {notice && (
          <div className="mb-4" data-testid="profile-edit-notice-container">
            <InlineNotice
              variant={notice.variant}
              title={notice.title}
              actionLabel={notice.actionLabel}
              onAction={notice.onAction}
              href={notice.href}
              testId="profile-edit-notice"
            >
              {notice.message}
            </InlineNotice>
          </div>
        )}

        {/* Legacy error/success (fallback) */}
        {error && !notice && (
          <div className="mb-4 rounded-lg bg-red-900/30 border border-red-500 px-4 py-3 text-red-200">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Basic Info */}
          <div className="rounded-lg bg-gray-900/60 border border-gray-700 p-6">
            <h2 className="mb-4 text-lg font-semibold text-white">Basic Info</h2>
            
            <div className="space-y-4">
              {/* Display Name */}
              <div>
                <label
                  htmlFor="display_name"
                  className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                >
                  Display Name
                </label>
                <input
                  type="text"
                  id="display_name"
                  name="display_name"
                  value={formData.display_name}
                  onChange={handleInputChange}
                  placeholder="How should we call you?"
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                />
              </div>

              {/* Date of Birth */}
              <div>
                <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-300 mb-2">
                  Date of Birth
                </label>
                <input
                  type="date"
                  id="date_of_birth"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2 text-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                />
              </div>

              {/* Gender */}
              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-300 mb-2">
                  Gender
                </label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2 text-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                >
                  <option value="">Select gender</option>
                  <option value="man">Man</option>
                  <option value="woman">Woman</option>
                  <option value="nonbinary">Non-binary</option>
                  <option value="other">Other</option>
                </select>
                {/* REMOVED: "couple" gender option - couples are now created via CoupleProfile */}
              </div>

              {/* Relationship Style */}
              <div>
                <label htmlFor="relationship_style" className="block text-sm font-medium text-gray-300 mb-2">
                  Relationship Style
                </label>
                <select
                  id="relationship_style"
                  name="relationship_style"
                  value={formData.relationship_style}
                  onChange={handleInputChange}
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2 text-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                >
                  <option value="">Select style</option>
                  <option value="monogamous">Monogamous</option>
                  <option value="polygamous">Polygamous</option>
                  <option value="polyamorous">Polyamorous</option>
                  <option value="open">Open</option>
                  <option value="exploring">Exploring</option>
                </select>
              </div>

              {/* Intent Signal v1 */}
              <div>
                <label htmlFor="intent_signal" className="block text-sm font-medium text-gray-300 mb-2">
                  What are you looking for right now?
                </label>
                <select
                  id="intent_signal"
                  name="intent_signal"
                  value={formData.intent_signal || ""}
                  onChange={handleInputChange}
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2 text-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                  data-testid="intent-signal-select"
                >
                  <option value="">Not specified</option>
                  <option value="serious">Serious</option>
                  <option value="friends_first">Friends-first</option>
                  <option value="casual">Casual</option>
                  <option value="exploring">Exploring</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  This shows as a badge on your profile cards
                </p>
              </div>
            </div>
          </div>

          {/* REMOVED: Couple Type & Couple Names sections - these are now in CoupleProfile */}

          {/* Section 2: What You're Looking For */}
          <div className="rounded-lg bg-gray-900/60 border border-gray-700 p-6">
            <h2 className="mb-4 text-lg font-semibold text-white">What You&apos;re Looking For</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                I&apos;m interested in (select all that apply)
              </label>
              <div className="flex flex-wrap gap-2">
                {["men", "women", "nonbinary", "couples", "everyone"].map(option => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleLookingForToggle(option)}
                    data-testid={`looking-for-${option}`}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                      formData.looking_for.includes(option)
                        ? "bg-purple-600 text-white"
                        : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                    }`}
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Profile Summary Preview */}
            {(formData.gender || formData.looking_for.length > 0) && (
              <div className="mt-5 pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-400 mb-2">Your profile will show:</p>
                <div className="bg-gray-800/50 rounded-lg px-4 py-3 text-white" data-testid="looking-for-summary">
                  <span className="font-medium">
                    {formData.gender ? (
                      <>{formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1)}</>
                    ) : (
                      <>Not specified</>
                    )}
                  </span>
                  {formData.looking_for.length > 0 && (
                    <>
                      <span className="text-gray-400"> seeking </span>
                      <span className="font-medium">
                        {formData.looking_for.includes('everyone') 
                          ? 'everyone'
                          : formData.looking_for.map((item, i) => {
                              const label = item === 'nonbinary' ? 'non-binary' : item;
                              const isLast = i === formData.looking_for.length - 1;
                              const isSecondLast = i === formData.looking_for.length - 2;
                              if (formData.looking_for.length === 1) return label;
                              if (isLast) return `& ${label}`;
                              if (isSecondLast) return `${label} `;
                              return `${label}, `;
                            }).join('')
                        }
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Location */}
          <div className="rounded-lg bg-gray-900/60 border border-gray-700 p-6">
            <h2 className="mb-4 text-lg font-semibold text-white">Location</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="location_city" className="block text-sm font-medium text-gray-300 mb-2">
                  City
                </label>
                <input
                  type="text"
                  id="location_city"
                  name="location_city"
                  value={formData.location_city}
                  onChange={handleInputChange}
                  placeholder="e.g., Montgomery"
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                />
              </div>

              <div>
                <label htmlFor="location_region" className="block text-sm font-medium text-gray-300 mb-2">
                  State / Region / Country
                </label>
                <input
                  type="text"
                  id="location_region"
                  name="location_region"
                  value={formData.location_region}
                  onChange={handleInputChange}
                  placeholder="e.g., AL, USA"
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                />
              </div>
            </div>
          </div>

          {/* Section 4: About You */}
          <div className="rounded-lg bg-gray-900/60 border border-gray-700 p-6">
            <h2 className="mb-4 text-lg font-semibold text-white">About You</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="bio" className="block text-sm font-medium text-gray-300 mb-2">
                  Bio
                </label>
                <textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Tell others about yourself..."
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                />
              </div>

              <div>
                <label htmlFor="interests" className="block text-sm font-medium text-gray-300 mb-2">
                  Interests (comma-separated)
                </label>
                <input
                  type="text"
                  id="interests"
                  name="interests"
                  value={formData.interests}
                  onChange={handleInputChange}
                  placeholder="e.g., music, travel, cooking"
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-2 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                />
              </div>
            </div>
          </div>

          {/* Section 5: Relationship Style & Structure (NEW - Poly/Polygamy Support) */}
          <RelationshipProfileSection 
            token={token}
            onSave={() => {
              setSuccess(true);
              setTimeout(() => setSuccess(false), 2000);
            }}
          />

          {/* Section 6: Children & Family */}
          <ChildrenSection 
            formData={formData}
            onChange={handleInputChange}
          />

          {/* Section 7: Main Photo (V2 Single Photo System) */}
          <MainPhotoSection 
            avatarUrl={avatarUrl}
            avatarUpdatedAt={avatarUpdatedAt}
            onAvatarUpdate={(url, updatedAt) => {
              setAvatarUrl(url);
              setAvatarUpdatedAt(updatedAt);
            }}
            token={token}
          />

          {/* Section 8: Video Intro - 🔧 FIX Issue #2 */}
          <VideoIntroSection
            videoUrl={videoIntroUrl}
            onVideoUpdate={(url, isUploading) => {
              setVideoIntroUrl(url);
              setUploadingVideo(isUploading);
            }}
            uploading={uploadingVideo}
            token={token}
          />

          {/* Section 9: Private Photos - Visible only to approved connections */}
          <PrivatePhotosSection
            privatePhotos={privatePhotos}
            onPhotosUpdate={setPrivatePhotos}
            uploading={uploadingPrivatePhoto}
            onUploadingChange={setUploadingPrivatePhoto}
            token={token}
          />

          {/* Section 10: Private Video - Visible only to approved connections */}
          <PrivateVideoSection
            privateVideos={privateVideos}
            onVideoUpdate={setPrivateVideos}
            uploading={uploadingPrivateVideo}
            onUploadingChange={setUploadingPrivateVideo}
            token={token}
          />

          {/* Submit Button - Sticky above watermark */}
          <div className="sticky bottom-4 z-50 mt-8 flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="rounded-lg border border-gray-600 bg-gray-800 px-6 py-2 text-white hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 rounded-lg bg-purple-600 px-6 py-2 text-white hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-wait"
            >
              {saving ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Profile</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    
    {/* Profile Preview Modal - Canonical viewer with draft override */}
    <ProfileViewModal
      open={showPreview}
      onClose={() => setShowPreview(false)}
      profileOverride={fullProfile ? {
        ...fullProfile,
        // Include draft media state
        profile_photos: getProfilePhotos(fullProfile).length > 0 
          ? getProfilePhotos(fullProfile) 
          : (avatarUrl ? [{ url: avatarUrl, position: 0 }] : []),
        video_intro_url: videoIntroUrl,
        avatar_url: avatarUrl,
        // Include draft form fields
        ...formData,
      } : null}
      isSelf={true}
    />
    </>
  );
}
